import { getStrategyData } from "containers/admin/clients/client/programs-page/program-strategy-form.utils";

import type { JsonFormOption } from "containers/admin/clients/client/components/form-builder/form-schema";

export type CreativeRef = {
  creativeId: string;
  name: string;
};

export type SmsChannelRow = {
  creativeId: string;
  name: string;
  option?: string;
  value?: string;
  daysAfter?: string;
};

const SMS_CHANNEL = "sms";
const EMAIL_CHANNEL = "email";
const UNNAMED_CREATIVE_NAME = "Unnamed";

// Numbering/ordering is product-confirmed: student items fully, then parent items fully, each in
// its own array order, filtered to the target channel — not creation order or a merged sort.
const AUDIENCE_KEYS = ["student", "parent"] as const;

type AudienceCreativeItem = { id?: unknown; channel?: unknown; name?: unknown };

const getAudienceCreativeItems = (
  draftData: unknown,
  audienceKey: (typeof AUDIENCE_KEYS)[number],
): AudienceCreativeItem[] => {
  const creativesData = getStrategyData(getStrategyData(draftData).creatives);
  const audienceData = getStrategyData(creativesData[audienceKey]);
  const items = audienceData.items;

  return Array.isArray(items) ? (items as AudienceCreativeItem[]) : [];
};

const deriveCreativeRefsByChannel = (draftData: unknown, channel: string): CreativeRef[] =>
  AUDIENCE_KEYS.flatMap((audienceKey) => getAudienceCreativeItems(draftData, audienceKey))
    .filter((item) => item.channel === channel)
    .map((item) => ({
      creativeId: typeof item.id === "string" ? item.id : "",
      name: typeof item.name === "string" && item.name.trim() ? item.name.trim() : UNNAMED_CREATIVE_NAME,
    }))
    .filter((ref) => ref.creativeId !== "");

export const deriveSmsCreativeRefs = (draftData: unknown): CreativeRef[] =>
  deriveCreativeRefsByChannel(draftData, SMS_CHANNEL);

export const deriveEmailCreativeRefs = (draftData: unknown): CreativeRef[] =>
  deriveCreativeRefsByChannel(draftData, EMAIL_CHANNEL);

export const reconcileSmsChannelRows = (
  existingRows: SmsChannelRow[],
  smsRefs: CreativeRef[],
  emailRefs: CreativeRef[],
): SmsChannelRow[] => {
  const existingRowsByCreativeId = new Map(existingRows.map((row) => [row.creativeId, row]));
  const validEmailCreativeIds = new Set(emailRefs.map((ref) => ref.creativeId));

  return smsRefs.map((ref) => {
    const existingRow = existingRowsByCreativeId.get(ref.creativeId);
    // Orphan handling: a saved `value` (an email creative id) whose target no longer exists is
    // cleared rather than left pointing at a no-longer-offered option.
    const value =
      existingRow?.value !== undefined && validEmailCreativeIds.has(existingRow.value)
        ? existingRow.value
        : undefined;

    return {
      creativeId: ref.creativeId,
      // The creative's own name can change independently of this row, so this always refreshes
      // rather than carrying over the stored `name`.
      name: ref.name,
      option: existingRow?.option,
      value,
      daysAfter: existingRow?.daysAfter,
    };
  });
};

// Email and SMS creatives are disjoint sets, so an SMS row can never reference itself here — no
// self-exclusion is needed.
export const getSmsValueOptions = (emailRefs: CreativeRef[]): JsonFormOption[] =>
  emailRefs.map((ref) => ({ label: ref.name, value: ref.creativeId }));
